import { E_DELIVERY_TYPE, E_CART_DELIVERY_TYPE } from '@/schema'
import type { TypeCmsVariant, TypeCmsCartLine, TypeCmsProduct } from '@/schema'

type CanAddToCartParams = {
  variant: TypeCmsVariant
  cartLines: TypeCmsCartLine[]
  cartDeliveryType?: E_CART_DELIVERY_TYPE
  currentQuantity?: number
  quantityToAdd?: number
}

type CanAddToCartResult = {
  canAdd: boolean
  reason?: string
}

const hasFreshProducts = (cartLines: TypeCmsCartLine[]): boolean => {
  return cartLines.some(
    (line) =>
      line.variant.deliveryType === E_DELIVERY_TYPE.STOCKED ||
      line.variant.deliveryType === E_DELIVERY_TYPE.ON_DEMAND
  )
}

const hasFrozenProducts = (cartLines: TypeCmsCartLine[]): boolean => {
  return cartLines.some(
    (line) => line.variant.deliveryType === E_DELIVERY_TYPE.FROZEN
  )
}

const checkStockAvailability = (
  variant: TypeCmsVariant,
  product: TypeCmsProduct | undefined,
  currentQuantity: number,
  quantityToAdd: number
): boolean => {
  const stock = variant.stock || 0
  const inventoryTracking = product?.inventoryTracking

  if (
    variant.deliveryType === E_DELIVERY_TYPE.ON_DEMAND &&
    inventoryTracking === false
  ) {
    return true
  }

  const totalQuantity = currentQuantity + quantityToAdd
  return stock >= totalQuantity
}

export const canAddToCart = ({
  variant,
  cartLines,
  cartDeliveryType,
  currentQuantity = 0,
  quantityToAdd = 1,
}: CanAddToCartParams): CanAddToCartResult => {
  const { deliveryType } = variant
  const product = variant.product
  const inventoryTracking = product?.inventoryTracking

  const cartLinesExcludingCurrent = cartLines.filter((line) => line.variant.id !== variant.id)

  if (deliveryType === E_DELIVERY_TYPE.FROZEN) {
    if (hasFreshProducts(cartLinesExcludingCurrent)) {
      return {
        canAdd: false,
        reason: 'Frozen products cannot be added to a cart with fresh products',
      }
    }
    if (cartDeliveryType === E_CART_DELIVERY_TYPE.FRESH) {
      return {
        canAdd: false,
        reason: 'Frozen products cannot be added to a fresh cart',
      }
    }

    const hasStock = checkStockAvailability(
      variant,
      product,
      currentQuantity,
      quantityToAdd
    )

    if (!hasStock) {
      return {
        canAdd: false,
        reason: 'Insufficient stock available',
      }
    }

    return { canAdd: true }
  }

  if (
    deliveryType === E_DELIVERY_TYPE.STOCKED ||
    deliveryType === E_DELIVERY_TYPE.ON_DEMAND
  ) {
    if (hasFrozenProducts(cartLinesExcludingCurrent)) {
      return {
        canAdd: false,
        reason: 'Fresh products cannot be added to a cart with frozen products',
      }
    }
    if (cartDeliveryType === E_CART_DELIVERY_TYPE.FROZEN) {
      return {
        canAdd: false,
        reason: 'Fresh products cannot be added to a frozen cart',
      }
    }

    if (
      deliveryType === E_DELIVERY_TYPE.ON_DEMAND &&
      inventoryTracking === false
    ) {
      return { canAdd: true }
    }

    const hasStock = checkStockAvailability(
      variant,
      product,
      currentQuantity,
      quantityToAdd
    )

    if (!hasStock) {
      return {
        canAdd: false,
        reason: 'Insufficient stock available',
      }
    }

    return { canAdd: true }
  }

  return { canAdd: true }
}

export const canAddMoreQuantity = (
  variant: TypeCmsVariant,
  currentQuantity: number,
  minSaleUnits: number
): boolean => {
  const stock = variant.stock || 0
  const product = variant.product
  const inventoryTracking = product?.inventoryTracking

  if (
    variant.deliveryType === E_DELIVERY_TYPE.ON_DEMAND &&
    inventoryTracking === false
  ) {
    return true
  }

  return stock >= currentQuantity + minSaleUnits
}
